import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Loader2,
  BarChart3,
  MessageSquare,
  ArrowLeftRight,
} from "lucide-react";
import useGlobalProgressStore, {
  ProgressEntry,
} from "@/store/GlobalProgressStore";

const ICON_MAP: Record<ProgressEntry["type"], typeof Loader2> = {
  analysis: BarChart3,
  research_assistant: MessageSquare,
  comparison: ArrowLeftRight,
};

// Extract the project id from a Results URL. Returns null when the
// current path isn't inside a project Results view. Pattern matches the
// route used in `/organization/.../project-results/:projectId`.
const PROJECT_RESULTS_PATTERN = /\/project-results\/([a-f0-9]{24})(?:[/?]|$)/i;

function extractOwningProjectIdFromResultsPath(
  pathname: string,
): string | null {
  const match = pathname.match(PROJECT_RESULTS_PATTERN);
  return match ? match[1] : null;
}

function ElapsedTime({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return (
    <span className="text-[11px] text-neutral-400 tabular-nums">
      {mins > 0 ? `${mins}m ${secs.toString().padStart(2, "0")}s` : `${secs}s`}
    </span>
  );
}

function GlobalProgressBar() {
  const entries = useGlobalProgressStore((s) => s.entries);
  const location = useLocation();
  const navigate = useNavigate();

  // Hide the bar for entries whose project is the one the user is
  // currently viewing IN RESULTS. Every project Results tab has its own
  // inline indicator (Running pill, RA tool stepper, comparison progress)
  // so a cross-cutting bar is just noise there. Everywhere else — Design,
  // Share, dashboard list, other projects — those inline indicators are
  // absent, so the bar stays visible to preserve cross-navigation
  // awareness.
  const visibleEntries = useMemo(() => {
    const ownerOnResultsView = extractOwningProjectIdFromResultsPath(
      location.pathname,
    );
    if (!ownerOnResultsView) return entries;
    return entries.filter((e) => e.projectId !== ownerOnResultsView);
  }, [entries, location.pathname]);

  if (visibleEntries.length === 0) return null;

  return (
    <div
      className="fixed left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-200 shadow-sm"
      style={{ top: 55 }}
    >
      {visibleEntries.map((entry) => {
        const Icon = ICON_MAP[entry.type] || Loader2;
        return (
          <button
            key={entry.id}
            type="button"
            onClick={() => navigate(entry.returnUrl)}
            className="flex items-center gap-2.5 w-full px-4 py-2 hover:bg-neutral-50 transition-colors text-left"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600 shrink-0" />
            <Icon className="h-3.5 w-3.5 text-neutral-500 shrink-0" />
            <span className="text-xs font-medium text-neutral-700 truncate">
              {entry.label}
            </span>
            {entry.progress && (
              <span className="text-[11px] text-neutral-500">
                {entry.progress.step}
                {entry.progress.total > 0 &&
                  ` ${entry.progress.completed}/${entry.progress.total}`}
              </span>
            )}
            <ElapsedTime startedAt={entry.startedAt} />
          </button>
        );
      })}
    </div>
  );
}

export default GlobalProgressBar;
